import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, RefreshCw, Search, Trash2, Printer, Truck, Cloud, Pencil } from "lucide-react";

const CUSTOMER_EMPTY = {
  full_name: "",
  phone: "",
  city: "",
  town: "",
  district: "",
  address_line: "",
  notes: "",
};

const LOCATION_EMPTY = {
  cityId: "",
  townId: "",
  districtId: "",
};

const TERMINAL_STATUSES = new Set(["DELIVERED", "TESLIM_EDILDI", "CANCELLED", "IPTAL", "RETURNED", "IADE"]);
const STATUS_TABS = [
  { key: "new", label: "Teslim Alınacaklar" },
  { key: "in_transit", label: "Dağıtım Sürecinde" },
  { key: "branch", label: "Teslimat Şubesinde" },
  { key: "courier", label: "Kurye Dağıtıma Çıktı" },
  { key: "delivered", label: "Teslim Edilenler" },
];

function extractRows(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.items)) return raw.items;
  if (raw?.data && typeof raw.data === "object") {
    for (const value of Object.values(raw.data)) {
      if (Array.isArray(value)) return value;
    }
  }
  if (raw && typeof raw === "object") {
    for (const value of Object.values(raw)) {
      if (Array.isArray(value)) return value;
    }
  }
  return [];
}

function toOptions(raw) {
  const rows = extractRows(raw);
  return rows
    .map((row) => {
      const id = row?.id ?? row?.cityId ?? row?.townId ?? row?.districtId ?? row?.code ?? "";
      const label = row?.name ?? row?.cityName ?? row?.townName ?? row?.districtName ?? row?.label ?? "";
      return { id: String(id || ""), label: String(label || "").trim() };
    })
    .filter((x) => x.id && x.label);
}

function normalizeText(v) {
  return String(v || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();
}

function resolveStatus(shipment) {
  const raw = String(shipment?.status || "").trim();
  const text = normalizeText(raw);
  if (!text) return "-";
  if (text.includes("teslim edildi") || text.includes("delivered")) return "Teslim Edildi";
  if (text.includes("kurye dağıtıma çıktı") || text.includes("dagitima cikti") || text.includes("out_for_delivery")) return "Kurye Dağıtıma Çıktı";
  if (text.includes("teslimat şubesinde") || text.includes("teslimat subesinde") || text.includes("sube") || text.includes("branch")) return "Teslimat Şubesinde";
  if (text.includes("dağıtım sürecinde") || text.includes("dagitim surecinde") || text.includes("transfer") || text.includes("yolda") || text.includes("transit")) return "Dağıtım Sürecinde";
  if (text.includes("pickup") || text.includes("accepted") || text.includes("accept") || text.includes("created")) return "Teslim Alınacak";
  if (text.includes("cancel") || text.includes("iptal")) return "İptal";
  return raw;
}

function statusBucket(statusRaw) {
  const s = normalizeText(statusRaw);
  if (!s) return "new";
  if (
    s.includes("teslim edildi") || s.includes("delivered") ||
    s.includes("recipient") || s.includes("aliciya teslim") || s.includes("teslim_alindi")
  ) return "delivered";
  if (
    s.includes("kurye dağıtıma çıktı") || s.includes("dagitima cikti") ||
    s.includes("out_for_delivery") || s.includes("out for delivery") ||
    s.includes("dagitimda") || s.includes("dağıtımda")
  ) return "courier";
  if (
    s.includes("teslimat şubesinde") || s.includes("teslimat subesinde") ||
    s.includes("sube") || s.includes("branch") || s.includes("hub") ||
    s.includes("şubede") || s.includes("subede")
  ) return "branch";
  if (
    s.includes("dağıtım sürecinde") || s.includes("dagitim surecinde") ||
    s.includes("transfer") || s.includes("yolda") || s.includes("transit") ||
    s.includes("sort") || s.includes("warehouse") || s.includes("on vehicle") ||
    s.includes("kargoda") || s.includes("kargoya verildi") || s.includes("shipped") ||
    s.includes("dispatch") || s.includes("in_transit") || s.includes("intransit")
  ) return "in_transit";
  return "new";
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr).slice(0, 16);
  }
}

export default function HepsiJetManualShipmentsPage() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [syncingStatuses, setSyncingStatuses] = useState(false);
  const autoTrackedOnMount = useRef(false);

  const [customers, setCustomers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [customerPickerQuery, setCustomerPickerQuery] = useState("");
  const [shipmentSearch, setShipmentSearch] = useState("");
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([]);

  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState("");
  const [customerForm, setCustomerForm] = useState(CUSTOMER_EMPTY);
  const [locationIds, setLocationIds] = useState(LOCATION_EMPTY);
  const [cities, setCities] = useState([]);
  const [towns, setTowns] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [shipmentForm, setShipmentForm] = useState({
    desi: "1",
    create_count: "1",
    notes: "",
  });

  const loadCustomers = useCallback(async () => {
    const res = await api.get("/hepsijet/manual/customers?page=1&per_page=500&search=");
    const rows = Array.isArray(res?.items) ? res.items : [];
    setCustomers(rows);
    if (selectedCustomerId) {
      const hasSelected = rows.some((row) => String(row?.id || "") === String(selectedCustomerId || ""));
      if (!hasSelected) {
        setSelectedCustomerId("");
        setCustomerPickerQuery("");
      }
    }
    return rows;
  }, [selectedCustomerId, setCustomerPickerQuery]);

  const loadShipments = useCallback(async () => {
    const res = await api.get(`/hepsijet/manual/shipments?page=1&per_page=200&search=${encodeURIComponent(shipmentSearch || "")}`);
    const rows = Array.isArray(res?.items) ? res.items : [];
    setShipments(rows);
    return rows;
  }, [shipmentSearch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadCustomers(), loadShipments()]);
    } catch (err) {
      toast.error(err.message || "HepsiJet kayıtları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [loadCustomers, loadShipments]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const selectedCustomer = useMemo(
    () => customers.find((x) => String(x.id) === String(selectedCustomerId)) || null,
    [customers, selectedCustomerId]
  );
  const filteredCustomers = useMemo(() => {
    const q = normalizeText(customerPickerQuery);
    if (!q) return customers.slice(0, 20);
    return customers
      .filter((c) => normalizeText(`${c?.full_name || ""} ${c?.phone || ""}`).includes(q))
      .slice(0, 20);
  }, [customers, customerPickerQuery]);

  const selectedSet = useMemo(() => new Set(selectedShipmentIds), [selectedShipmentIds]);
  const filteredShipments = useMemo(() => {
    const q = normalizeText(shipmentSearch);
    return shipments.filter((row) => {
      if (statusBucket(row?.status) !== activeTab) return false;
      if (!q) return true;
      const hay = normalizeText(
        `${row?.barcode || ""} ${row?.customer_name || ""} ${row?.customer_phone || ""} ${row?.reference_no || ""} ${row?.status || ""}`
      );
      return hay.includes(q);
    });
  }, [shipments, shipmentSearch, activeTab]);
  const tabCounts = useMemo(() => {
    const counts = { new: 0, in_transit: 0, branch: 0, courier: 0, delivered: 0 };
    shipments.forEach((row) => {
      const key = statusBucket(row?.status);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [shipments]);
  const allSelected = filteredShipments.length > 0 && filteredShipments.every((x) => selectedSet.has(String(x.id)));

  useEffect(() => {
    if (!selectedCustomer) return;
    setCustomerPickerQuery(`${selectedCustomer.full_name || ""} (${selectedCustomer.phone || ""})`);
  }, [selectedCustomer]);

  const resetCustomerForm = () => {
    setCustomerForm(CUSTOMER_EMPTY);
    setEditingCustomerId("");
    setLocationIds(LOCATION_EMPTY);
    setTowns([]);
    setDistricts([]);
  };

  const loadCities = useCallback(async () => {
    const res = await api.get("/hepsijet/cities");
    setCities(toOptions(res));
  }, []);

  const loadTowns = useCallback(async (cityId) => {
    if (!cityId) {
      setTowns([]);
      return;
    }
    const res = await api.get(`/hepsijet/towns/${cityId}`);
    setTowns(toOptions(res));
  }, []);

  const loadDistricts = useCallback(async (townId) => {
    if (!townId) {
      setDistricts([]);
      return;
    }
    const res = await api.get(`/hepsijet/districts/${townId}`);
    setDistricts(toOptions(res));
  }, []);

  const openCreateCustomer = async () => {
    resetCustomerForm();
    setCustomerModalOpen(true);
    try {
      await loadCities();
    } catch (err) {
      toast.error(err.message || "Şehirler alınamadı");
    }
  };

  const openEditCustomer = async (customer) => {
    setEditingCustomerId(String(customer?.id || ""));
    setCustomerForm({
      full_name: String(customer?.full_name || ""),
      phone: String(customer?.phone || ""),
      city: String(customer?.city || ""),
      town: String(customer?.town || ""),
      district: String(customer?.district || ""),
      address_line: String(customer?.address_line || ""),
      notes: String(customer?.notes || ""),
    });
    setLocationIds(LOCATION_EMPTY);
    setCustomerModalOpen(true);
    try {
      const cityRes = await api.get("/hepsijet/cities");
      const cityOptions = toOptions(cityRes);
      setCities(cityOptions);

      const targetCity = cityOptions.find((x) => normalizeText(x.label) === normalizeText(customer?.city));
      if (!targetCity) return;

      setLocationIds((prev) => ({ ...prev, cityId: targetCity.id }));
      const townRes = await api.get(`/hepsijet/towns/${targetCity.id}`);
      const townOptions = toOptions(townRes);
      setTowns(townOptions);

      const targetTown = townOptions.find((x) => normalizeText(x.label) === normalizeText(customer?.town));
      if (!targetTown) return;

      setLocationIds((prev) => ({ ...prev, cityId: targetCity.id, townId: targetTown.id }));
      const districtRes = await api.get(`/hepsijet/districts/${targetTown.id}`);
      const districtOptions = toOptions(districtRes);
      setDistricts(districtOptions);

      const targetDistrict = districtOptions.find((x) => normalizeText(x.label) === normalizeText(customer?.district));
      if (targetDistrict) {
        setLocationIds((prev) => ({ ...prev, cityId: targetCity.id, townId: targetTown.id, districtId: targetDistrict.id }));
      }
    } catch (err) {
      toast.error(err.message || "Konum bilgileri alınamadı");
    }
  };

  const saveCustomer = async () => {
    let savedCustomer = null;
    try {
      if (!customerForm.full_name || !customerForm.phone || !customerForm.city || !customerForm.town || !customerForm.district || !customerForm.address_line) {
        toast.error("Müşteri adı, telefon, il, ilçe, mahalle ve adres zorunlu");
        return;
      }
      setSavingCustomer(true);

      const payload = {
        full_name: customerForm.full_name,
        phone: customerForm.phone,
        email: "",
        city: customerForm.city,
        town: customerForm.town,
        district: customerForm.district,
        address_line: customerForm.address_line,
        postal_code: "",
        notes: customerForm.notes || "",
      };

      if (editingCustomerId) {
        const res = await api.put(`/hepsijet/manual/customers/${editingCustomerId}`, payload);
        savedCustomer = res?.customer || null;
        toast.success("Müşteri güncellendi");
      } else {
        const res = await api.post("/hepsijet/manual/customers", payload);
        savedCustomer = res?.customer || null;
        toast.success("Müşteri oluşturuldu");
      }

      if (savedCustomer?.id) {
        setCustomers((prev) => {
          const key = String(savedCustomer.id);
          const filtered = prev.filter((x) => String(x.id) !== key);
          return [savedCustomer, ...filtered];
        });
        setSelectedCustomerId(String(savedCustomer.id));
      }

      setCustomerModalOpen(false);
      resetCustomerForm();

      // Silent refresh; do not convert a successful save into an error toast.
      loadCustomers().catch(() => {});
    } catch (err) {
      toast.error(err.message || "Müşteri kaydedilemedi");
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    const customerId = String(customer?.id || "");
    if (!customerId) return;
    if (!window.confirm("Müşteri silinsin mi?")) return;
    try {
      await api.delete(`/hepsijet/manual/customers/${customerId}`);
      toast.success("Müşteri silindi");
      setCustomers((prev) => prev.filter((x) => String(x.id) !== customerId));
      if (String(selectedCustomerId || "") === customerId) {
        setSelectedCustomerId("");
        setCustomerPickerQuery("");
      }
    } catch (err) {
      toast.error(err.message || "Müşteri silinemedi");
    }
  };


  const refreshShipmentsWithRetry = async () => {
    await loadShipments();
    setTimeout(() => {
      loadShipments().catch(() => {});
    }, 1200);
    setTimeout(() => {
      loadShipments().catch(() => {});
    }, 2800);
  };

  const handleCreateShipment = async () => {
    if (!selectedCustomerId) {
      toast.error("Müşteri seçin");
      return;
    }
    const createCount = Math.max(1, Number(shipmentForm.create_count || "1"));
    const payload = {
      customer_id: selectedCustomerId,
      desi: Number(shipmentForm.desi || "1"),
      total_parcels: 1,
      reference_no: "",
      notes: shipmentForm.notes || "",
    };
    const createStartedAt = Date.now();
    const beforeCount = shipments.length;
    setCreating(true);
    try {
      if (createCount === 1) {
        const res = await api.post("/hepsijet/manual/shipments", payload);
        if (res?.success === false) throw new Error(res?.message || "Etiket oluşturulamadı");
        if (res?.shipment?.id) {
          setShipments((prev) => [res.shipment, ...prev.filter((x) => String(x.id) !== String(res.shipment.id))]);
        }
      } else {
        const res = await api.post("/hepsijet/manual/shipments/bulk", {
          shipments: Array.from({ length: createCount }, () => payload),
        });
        const results = Array.isArray(res?.results) ? res.results : [];
        const failed = results.filter((x) => !x?.success);
        if (failed.length) {
          const firstError = String(failed[0]?.message || "Bilinmeyen hata");
          throw new Error(`${failed.length}/${results.length || createCount} etiket başarısız: ${firstError}`);
        }
        const created = results.map((x) => x?.shipment).filter((x) => x?.id);
        if (created.length) {
          setShipments((prev) => {
            const removeKeys = new Set(created.map((x) => String(x.id)));
            const filtered = prev.filter((x) => !removeKeys.has(String(x.id)));
            return [...created, ...filtered];
          });
        }
      }
      toast.success(`${createCount} etiket oluşturuldu`);
      refreshShipmentsWithRetry().catch(() => {});
    } catch (err) {
      try {
        const rows = await loadShipments();
        const recovered = rows.some((row) => {
          const createdAt = new Date(row?.created_at || 0).getTime();
          return (
            String(row?.customer_id || "") === String(selectedCustomerId || "") &&
            Number(row?.total_desi || row?.desi || 0) === Number(payload.desi || 0) &&
            createdAt >= createStartedAt - 15000
          );
        });
        if (recovered || rows.length > beforeCount) {
          toast.success("Etiket oluşturuldu. Liste güncellendi.");
          return;
        }
      } catch {}
      toast.error(err.message || "Etiket oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const trackShipment = useCallback(async (shipmentId, opts = { silent: false }) => {
    const silent = Boolean(opts?.silent);
    const res = await api.post(`/hepsijet/manual/shipments/${shipmentId}/track`);
    if (res?.shipment?.id) {
      setShipments((prev) => prev.map((x) => (String(x.id) === String(res.shipment.id) ? res.shipment : x)));
    }
    if (!silent) toast.success("Durum güncellendi");
  }, []);

  // Sayfa açılışında non-terminal gönderileri otomatik track et (trackShipment'tan sonra tanımlanmalı)
  useEffect(() => {
    if (autoTrackedOnMount.current) return;
    if (!shipments.length) return;
    autoTrackedOnMount.current = true;
    const targets = shipments
      .filter((x) => x?.barcode)
      .filter((x) => !TERMINAL_STATUSES.has(String(x?.status || "").toLocaleUpperCase("tr-TR")))
      .slice(0, 20);
    if (!targets.length) return;
    Promise.allSettled(targets.map((x) => trackShipment(x.id, { silent: true }))).catch(() => {});
  }, [shipments, trackShipment]);

  const handleTrack = async (shipmentId) => {
    try {
      await trackShipment(shipmentId, { silent: false });
    } catch (err) {
      toast.error(err.message || "Durum güncellenemedi");
    }
  };

  const handleTrackAll = async () => {
    const target = shipments
      .filter((x) => x?.barcode)
      .filter((x) => !TERMINAL_STATUSES.has(String(x?.status || "").toLocaleUpperCase("tr-TR")))
      .slice(0, 15)
      .map((x) => x.id);
    if (!target.length) {
      toast.info("Güncellenecek aktif gönderi yok");
      return;
    }
    setSyncingStatuses(true);
    try {
      const results = await Promise.allSettled(target.map((id) => trackShipment(id, { silent: true })));
      const failed = results.filter((x) => x.status === "rejected").length;
      if (failed) {
        toast.warning(`Durumlar güncellendi (${target.length - failed}/${target.length})`);
      } else {
        toast.success(`Durumlar güncellendi (${target.length})`);
      }
    } finally {
      setSyncingStatuses(false);
    }
  };

  useEffect(() => {
    if (!shipments.length) return undefined;
    const timer = setInterval(() => {
      const target = shipments
        .filter((x) => x?.barcode)
        .filter((x) => !TERMINAL_STATUSES.has(String(x?.status || "").toLocaleUpperCase("tr-TR")))
        .slice(0, 10)
        .map((x) => x.id);
      if (!target.length) return;
      Promise.allSettled(target.map((id) => trackShipment(id, { silent: true }))).catch(() => {});
    }, 60000);
    return () => clearInterval(timer);
  }, [shipments, trackShipment]);

  const decodePdfDataUrl = (b64, mime = "application/pdf") => {
    const bin = atob(b64 || "");
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: mime }));
  };

  const handleLabelView = async (shipmentId) => {
    try {
      const res = await api.get(`/hepsijet/manual/shipments/${shipmentId}/label?format=PDF`);
      let url = String(res?.file_url || "").trim();
      let isBlob = false;
      if (!url) {
        const b64 = String(res?.data_base64 || res?.data || "").trim();
        if (!b64) throw new Error("Etiket verisi boş");
        url = decodePdfDataUrl(b64, "application/pdf");
        isBlob = true;
      }
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened) throw new Error("Pop-up engellendi");
      if (isBlob) setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.message || "Etiket açılamadı");
    }
  };

  const handleCloudPrint = async (shipmentId) => {
    try {
      const res = await api.post(`/hepsijet/manual/shipments/${shipmentId}/cloud-print`);
      if (!res?.success) throw new Error(res?.message || "Bulut yazdırma kuyruğa eklenemedi");
      toast.success("Bulut yazdırma kuyruğa eklendi");
    } catch (err) {
      toast.error(err.message || "Bulut yazdırma başlatılamadı");
    }
  };

  const handleDeleteShipment = async (shipmentId) => {
    if (!window.confirm("Gönderi silinsin mi? (HepsiJet'ten de silinir)")) return;
    try {
      await api.delete(`/hepsijet/manual/shipments/${shipmentId}`);
      toast.success("Gönderi silindi");
      setSelectedShipmentIds((prev) => prev.filter((x) => String(x) !== String(shipmentId)));
      await loadShipments();
    } catch (err) {
      toast.error(err.message || "Gönderi silinemedi");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedShipmentIds.length) return;
    if (!window.confirm(`${selectedShipmentIds.length} gönderi silinsin mi?`)) return;
    setDeletingBulk(true);
    try {
      await api.delete("/hepsijet/manual/shipments", { data: { ids: selectedShipmentIds } });
      toast.success("Seçili gönderiler silindi");
      setSelectedShipmentIds([]);
      await loadShipments();
    } catch (err) {
      toast.error(err.message || "Toplu silme başarısız");
    } finally {
      setDeletingBulk(false);
    }
  };

  const onCityChange = async (cityId) => {
    const city = cities.find((x) => String(x.id) === String(cityId));
    setLocationIds({ cityId: String(cityId || ""), townId: "", districtId: "" });
    setCustomerForm((p) => ({ ...p, city: city?.label || "", town: "", district: "" }));
    setDistricts([]);
    await loadTowns(cityId);
  };

  const onTownChange = async (townId) => {
    const town = towns.find((x) => String(x.id) === String(townId));
    setLocationIds((prev) => ({ ...prev, townId: String(townId || ""), districtId: "" }));
    setCustomerForm((p) => ({ ...p, town: town?.label || "", district: "" }));
    await loadDistricts(townId);
  };

  const onDistrictChange = (districtId) => {
    const district = districts.find((x) => String(x.id) === String(districtId));
    setLocationIds((prev) => ({ ...prev, districtId: String(districtId || "") }));
    setCustomerForm((p) => ({ ...p, district: district?.label || "" }));
  };

  return (
    <div className="page-shell min-h-[calc(100dvh-3.5rem)] space-y-4">
      <div className="page-header">
        <div className="app-page-header">
          <h1 className="app-page-title flex items-center gap-2"><Truck className="h-6 w-6 text-sky-600" />HepsiJet</h1>
          <p className="app-page-subtitle">Müşteri kaydı oluşturup siparişten bağımsız kargo etiketi üretebilirsiniz.</p>
        </div>
        <div className="page-header-actions">
          <Button variant="outline" onClick={loadAll} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Yenile
          </Button>
        </div>
      </div>

      <div className="space-y-4">
          <div className="surface-card p-5">
            <div className="text-sm font-semibold mb-4">Etiket Oluştur</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Müşteri Ara</Label>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="h-4 w-4 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
                      <Input
                        className="pl-8"
                        placeholder="Ad soyad veya telefon ile ara..."
                        value={customerPickerQuery}
                        onChange={(e) => {
                          setCustomerPickerQuery(e.target.value);
                          setCustomerSearchOpen(Boolean(normalizeText(e.target.value)));
                        }}
                        onFocus={() => {
                          if (normalizeText(customerPickerQuery)) setCustomerSearchOpen(true);
                        }}
                      />
                    </div>
                    <Button size="sm" onClick={openCreateCustomer}>
                      <Plus className="h-4 w-4 mr-1" />
                      Ekle
                    </Button>
                  </div>
                  {customerSearchOpen && filteredCustomers.length > 0 ? (
                    <div className="mt-2 max-h-56 overflow-auto rounded-md border bg-card">
                      {filteredCustomers.map((c) => (
                        <div
                          key={c.id}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-muted/50 cursor-pointer ${
                            String(selectedCustomerId) === String(c.id) ? "bg-sky-50" : ""
                          }`}
                          onClick={() => {
                            setSelectedCustomerId(String(c.id));
                            setCustomerPickerQuery(`${c.full_name || ""} (${c.phone || ""})`);
                            setCustomerSearchOpen(false);
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-medium">{c.full_name}</div>
                              <div className="text-xs text-muted-foreground">{c.phone} · {c.district} / {c.town} / {c.city}</div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              onClick={(evt) => {
                                evt.preventDefault();
                                evt.stopPropagation();
                                openEditCustomer(c);
                              }}
                              title="Müşteriyi düzenle"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              onClick={(evt) => {
                                evt.preventDefault();
                                evt.stopPropagation();
                                handleDeleteCustomer(c);
                              }}
                              title="Müşteriyi sil"
                            >
                              <Trash2 className="h-4 w-4 text-rose-600" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : customerSearchOpen ? (
                    <div className="mt-2 text-xs text-muted-foreground">Müşteri bulunamadı</div>
                  ) : null}
                </div>
              </div>
              <div>
                <Label>Desi</Label>
                <Input value={shipmentForm.desi} onChange={(e) => setShipmentForm((p) => ({ ...p, desi: e.target.value }))} />
              </div>
              <div>
                <Label>Kaç Etiket Üretilecek</Label>
                <Input value={shipmentForm.create_count} onChange={(e) => setShipmentForm((p) => ({ ...p, create_count: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <Label>Not</Label>
                <Input value={shipmentForm.notes} onChange={(e) => setShipmentForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={handleTrackAll} disabled={syncingStatuses || !shipments.length}>
                  {syncingStatuses ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Durumları Güncelle
                </Button>
                <Button onClick={handleCreateShipment} disabled={creating || !selectedCustomerId}>
                  {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Oluştur
                </Button>
              </div>
            </div>
          </div>

          <div className="table-shell overflow-hidden">
            <div className="px-5 py-4 border-b flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="font-semibold">Oluşturulan Etiketler</div>
              <div className="flex w-full items-center gap-2 md:w-auto">
                <div className="flex items-center gap-2 rounded-lg border bg-background px-3 h-9 flex-1 md:flex-none md:w-[220px]">
                  <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                  <input className="flex-1 bg-transparent text-sm outline-none" placeholder="Ara..." value={shipmentSearch} onChange={(e) => setShipmentSearch(e.target.value)} />
                </div>
                <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={!selectedShipmentIds.length || deletingBulk}>
                  {deletingBulk ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  <span className="hidden sm:inline">Seçili Sil</span>
                </Button>
              </div>
            </div>
            <div className="p-4">
              <div className="mb-3 flex flex-wrap gap-2">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                      activeTab === tab.key
                        ? "border-sky-500 bg-sky-50 text-sky-700"
                        : "border-border bg-card text-muted-foreground hover:bg-muted/50"
                    }`}
                  >
                    {tab.label} ({tabCounts[tab.key] || 0})
                  </button>
                ))}
              </div>
              <div className="space-y-2 md:hidden">
                {filteredShipments.map((s) => (
                  <div key={s.id} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs text-muted-foreground">Barkod</div>
                        <div className="font-medium break-all">{s.barcode || "-"}</div>
                      </div>
                      <Checkbox
                        checked={selectedSet.has(String(s.id))}
                        onCheckedChange={(val) => {
                          setSelectedShipmentIds((prev) => {
                            const key = String(s.id);
                            if (val) return Array.from(new Set([...prev, key]));
                            return prev.filter((x) => String(x) !== key);
                          });
                        }}
                      />
                    </div>
                    <div className="mt-2 text-sm">
                      <div className="font-medium">{s.customer_name || "-"}</div>
                      <div className="text-xs text-muted-foreground">{s.customer_phone || "-"}</div>
                      <div className="mt-1 text-xs text-muted-foreground">Durum: {resolveStatus(s)}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(s.created_at)}</div>
                    </div>
                    <div className="mt-3 grid grid-cols-4 gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleTrack(s.id)}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleLabelView(s.id)}>
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleCloudPrint(s.id)} title="Bulut Yazdır">
                        <Cloud className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDeleteShipment(s.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-auto rounded-lg border md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-2 py-2 w-[36px] text-left">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(val) => setSelectedShipmentIds(val ? filteredShipments.map((x) => String(x.id)) : [])}
                        />
                      </th>
                      <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Barkod</th>
                      <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Müşteri</th>
                      <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground hidden md:table-cell">Desi</th>
                      <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground hidden md:table-cell">Koli</th>
                      <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground hidden md:table-cell">Oluşturma</th>
                      <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Durum</th>
                      <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground sticky right-0 bg-muted/50">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShipments.map((s) => (
                      <tr key={s.id} className="border-b">
                        <td className="px-2 py-2">
                          <Checkbox
                            checked={selectedSet.has(String(s.id))}
                            onCheckedChange={(val) => {
                              setSelectedShipmentIds((prev) => {
                                const key = String(s.id);
                                if (val) return Array.from(new Set([...prev, key]));
                                return prev.filter((x) => String(x) !== key);
                              });
                            }}
                          />
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">{s.barcode || "-"}</td>
                        <td className="px-2 py-2">
                          <div className="font-medium">{s.customer_name || "-"}</div>
                          <div className="text-xs text-muted-foreground hidden sm:block">{s.customer_phone || "-"}</div>
                        </td>
                        <td className="px-2 py-2 hidden md:table-cell">{s.desi}</td>
                        <td className="px-2 py-2 hidden md:table-cell">{s.total_parcels}</td>
                        <td className="px-2 py-2 hidden md:table-cell whitespace-nowrap text-xs text-muted-foreground">{formatDate(s.created_at)}</td>
                        <td className="px-2 py-2">{resolveStatus(s)}</td>
                        <td className="px-2 py-2 sticky right-0 bg-card">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleTrack(s.id)}>
                              <RefreshCw className="h-4 w-4 md:mr-1" />
                              <span className="hidden md:inline">Durum</span>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleLabelView(s.id)}>
                              <Printer className="h-4 w-4 md:mr-1" />
                              <span className="hidden md:inline">Yazdır</span>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleCloudPrint(s.id)} title="Bulut Yazdır">
                              <Cloud className="h-4 w-4 md:mr-1" />
                              <span className="hidden md:inline">Bulut</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDeleteShipment(s.id)}>
                              <Trash2 className="h-4 w-4 md:mr-1" />
                              <span className="hidden md:inline">Sil</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!filteredShipments.length ? <div className="text-sm text-muted-foreground mt-3">Bu sekmede gönderi yok.</div> : null}
            </div>
          </div>
      </div>

      <Dialog open={customerModalOpen} onOpenChange={setCustomerModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingCustomerId ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle>
            <DialogDescription>HepsiJet siparişsiz etiket için müşteri bilgisi.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Ad Soyad</Label>
              <Input value={customerForm.full_name} onChange={(e) => setCustomerForm((p) => ({ ...p, full_name: e.target.value }))} />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={customerForm.phone} onChange={(e) => setCustomerForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div>
              <Label>İl</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={locationIds.cityId}
                onChange={(e) => onCityChange(e.target.value)}
              >
                <option value="">İl seçin</option>
                {cities.map((x) => (
                  <option key={x.id} value={x.id}>{x.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>İlçe</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={locationIds.townId}
                onChange={(e) => onTownChange(e.target.value)}
                disabled={!locationIds.cityId}
              >
                <option value="">İlçe seçin</option>
                {towns.map((x) => (
                  <option key={x.id} value={x.id}>{x.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Mahalle</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={locationIds.districtId}
                onChange={(e) => onDistrictChange(e.target.value)}
                disabled={!locationIds.townId}
              >
                <option value="">Mahalle seçin</option>
                {districts.map((x) => (
                  <option key={x.id} value={x.id}>{x.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Adres</Label>
              <Input value={customerForm.address_line} onChange={(e) => setCustomerForm((p) => ({ ...p, address_line: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Not</Label>
              <Input value={customerForm.notes} onChange={(e) => setCustomerForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCustomerModalOpen(false)} disabled={savingCustomer}>Vazgeç</Button>
              <Button onClick={saveCustomer} disabled={savingCustomer}>
                {savingCustomer ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
